// components/SchoolSettingsSection.js
import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { 
  School, 
  Save,
  Camera,
  MapPin,
  Phone,
  Upload,
  Trash2,
  AlertCircle,
  Check,
  X,
  Percent,
  Users,
  Clock} from 'lucide-react';
import useSchoolParams from '../hooks/useSchoolParams';
import ConfirmDialog from './ConfirmDialog';
import { Toast } from "../components/ui/Toast";

const SchoolSettingsSection = ({ onBackToDashboard }) => {
  const { 
    schoolInfo, 
    handleInputChange, 
    updateSchoolParams,
    uploadLogo,
    deleteLogo,
    loading: schoolLoading 
  } = useSchoolParams();
  
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // États pour le dialog de confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // États pour le Toast
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({ message: '', type: 'success' });

  // Fonctions pour gérer le Toast
  const showSuccessToast = (message) => {
    setToastConfig({ message, type: 'success' });
    setShowToast(true);
  };

  const showErrorToast = (message) => {
    setToastConfig({ message, type: 'error' });
    setShowToast(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const result = await updateSchoolParams(schoolInfo);
      
      if (result.success) {
        setShowSuccess(true);
        setMessage({ 
          type: 'success', 
          text: 'Paramètres sauvegardés avec succès !' 
        });
        
        // Toast de succès
        showSuccessToast('Paramètres sauvegardés avec succès !');
        
        setTimeout(() => {
          setShowSuccess(false);
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        const errorMessage = result.error || 'Erreur lors de la sauvegarde';
        setMessage({ 
          type: 'error', 
          text: errorMessage 
        });
        
        // Toast d'erreur
        showErrorToast(errorMessage);
      }
    } catch (error) {
      const errorMessage = 'Erreur de connexion au serveur';
      setMessage({ 
        type: 'error', 
        text: errorMessage 
      });
      
      // Toast d'erreur
      showErrorToast(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation
    if (file.size > 2 * 1024 * 1024) {
      const errorMessage = 'Le fichier est trop volumineux (max 2MB)';
      setMessage({ 
        type: 'error', 
        text: errorMessage 
      });
      showErrorToast(errorMessage);
      return;
    }

    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'].includes(file.type)) {
      const errorMessage = 'Format non supporté (PNG, JPG, SVG uniquement)';
      setMessage({ 
        type: 'error', 
        text: errorMessage 
      });
      showErrorToast(errorMessage);
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const result = await uploadLogo(file);
      
      if (result.success) {
        const successMessage = 'Logo uploadé avec succès !';
        setMessage({ 
          type: 'success', 
          text: successMessage 
        });
        showSuccessToast(successMessage);
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const errorMessage = result.error || 'Erreur lors de l\'upload';
        setMessage({ 
          type: 'error', 
          text: errorMessage 
        });
        showErrorToast(errorMessage);
      }
    } catch (error) {
      const errorMessage = 'Erreur lors de l\'upload du logo';
      setMessage({ 
        type: 'error', 
        text: errorMessage 
      });
      showErrorToast(errorMessage);
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    try {
      const result = await deleteLogo();
      
      if (result.success) {
        const successMessage = 'Logo supprimé avec succès !';
        setMessage({ 
          type: 'success', 
          text: successMessage 
        });
        showSuccessToast(successMessage);
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const errorMessage = result.error || 'Erreur lors de la suppression';
        setMessage({ 
          type: 'error', 
          text: errorMessage 
        });
        showErrorToast(errorMessage);
      }
    } catch (error) {
      const errorMessage = 'Erreur lors de la suppression du logo';
      setMessage({ 
        type: 'error', 
        text: errorMessage 
      });
      showErrorToast(errorMessage);
    }
  };

  const clearMessage = () => {
    setMessage({ type: '', text: '' });
  };

  // Affichage de chargement
  if (schoolLoading) {
    return (
      <div className="w-full">
        <EnTeteParametrage
          onRetour={onBackToDashboard}
          icone={School}
          titre="Paramétrage de l'établissement"
          sousTitre="Identité, coordonnées et logo de votre école"
        />

        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
          <ChargementSection accent="#246990" libelle="Chargement des informations de l'école…" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={School}
        titre="Paramétrage de l'établissement"
        sousTitre="Identité, coordonnées et logo de votre école"
      />

      <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
        <div className="space-y-5">
          <EnTeteSection
            icone={School}
            titre="Identité de l'établissement"
            sousTitre="Nom, coordonnées, logo et informations affichées aux familles"
            accent="#246990"
            accentSombre="#1a4f6e"
          />

          {/* Message de feedback */}
          {message.text && (
            <div className={`mb-6 p-3 rounded-lg flex items-center justify-between ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <div className="flex items-center space-x-2">
                {message.type === 'success' ? (
                  <Check size={16} />
                ) : (
                  <AlertCircle size={16} />
                )}
                <span className="text-sm font-medium">{message.text}</span>
              </div>
              <button 
                onClick={clearMessage}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
          )}

          <CarteReglages titre="Informations de l'école" accent="#246990">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Colonne gauche - Logo et identité */}
            <div className="space-y-6">
              
              {/* Aperçu du logo */}
              <div className="space-y-4">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Camera size={16} className="text-blue-600" />
                  Logo de l'école
                </Label>
                
                {/* Container avec fond blanc pour le logo */}
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center p-2 shadow-sm border-2 border-gray-100">
                    {schoolInfo.logo_url ? (
                      <img 
                        src={schoolInfo.logo_url}
                        alt="Logo école"
                        className="max-w-full max-h-full object-contain rounded"
                      />
                    ) : (
                      <div 
                        className="w-full h-full flex items-center justify-center text-2xl font-bold rounded"
                        style={{ color: schoolInfo.couleur_logo || '#246990' }}
                      >
                        {schoolInfo.logo_initiales || 'EC'}
                      </div>
                    )}
                  </div>
                  
                  {/* Boutons upload/suppression */}
                  <div className="flex flex-col space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                      disabled={uploading}
                    />
                    <label
                      htmlFor="logo-upload"
                      className={`cursor-pointer px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 transition-colors flex items-center space-x-2 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload size={14} />
                      <span>{uploading ? 'Upload...' : 'Upload logo'}</span>
                    </label>
                    
                    {schoolInfo.logo_url && (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm hover:bg-red-100 transition-colors flex items-center space-x-2"
                      >
                        <Trash2 size={14} />
                        <span>Supprimer</span>
                      </button>
                    )}
                  </div>
                </div>
                
                <p className="text-xs text-gray-500">
                  Formats acceptés: PNG, JPG, SVG (max 2MB). Le logo sera automatiquement redimensionné.
                </p>
              </div>

              {/* Nom de l'école */}
              <div className="space-y-3">
                <Label htmlFor="schoolName" className="text-sm font-semibold text-gray-700">
                  Nom de l'établissement *
                </Label>
                <Input
                  id="schoolName"
                  value={schoolInfo.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Nom de votre école"
                  className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                />
              </div>

              {/* Sous-titre */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700">
                  Sous-titre *
                </Label>
                <Input
                  value={schoolInfo.sous_titre || ''}
                  onChange={(e) => handleInputChange('sous_titre', e.target.value)}
                  placeholder="Ex: Établissement Scolaire, Complexe Scolaire..."
                  className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                />
              </div>

              {/* Slogan */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700">
                  Slogan (optionnel)
                </Label>
                <Input
                  value={schoolInfo.slogan || ''}
                  onChange={(e) => handleInputChange('slogan', e.target.value)}
                  placeholder="Ex: Unité, Travail, Progrès"
                  className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                />
              </div>

                    {/* Textes de la page d'accueil */}
                <div className="space-y-4">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <School size={16} className="text-indigo-600" />
                    Textes de la page d'accueil
                </Label>
                
                <div className="space-y-3">
                    <div>
                    <Label className="text-sm font-medium text-gray-600">
                        Titre de bienvenue
                    </Label>
                    <Input
                        value={schoolInfo.titre_accueil || ''}
                        onChange={(e) => handleInputChange('titre_accueil', e.target.value)}
                        placeholder="Bienvenue dans votre espace"
                        className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                        maxLength={200}
                    />
                    </div>
                    
                    <div>
                    <Label className="text-sm font-medium text-gray-600">
                        Description d'accueil
                    </Label>
                    <textarea
                        value={schoolInfo.description_accueil || ''}
                        onChange={(e) => handleInputChange('description_accueil', e.target.value)}
                        placeholder="Les informations essentielles pour suivre la scolarité de vos enfants"
                        rows={3}
                        maxLength={500}
                        className="w-full p-2 border border-gray-200 rounded-md focus:border-blue-500 focus:ring-blue-500/20 resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Ces textes s'afficheront en haut de la page d'accueil pour tous les utilisateurs
                    </p>
                    </div>
                </div>
                </div>


              {/* Initiales du logo */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700">
                  Initiales du logo *
                </Label>
                <Input
                  value={schoolInfo.logo_initiales || ''}
                  onChange={(e) => handleInputChange('logo_initiales', e.target.value)}
                  maxLength="10"
                  placeholder="Ex: EC, AP, LM..."
                  className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                />
                <p className="text-xs text-gray-500">
                  Utilisées en fallback si pas d'image de logo
                </p>
              </div>

              {/* Couleur du logo */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700">
                  Couleur du logo
                </Label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={schoolInfo.couleur_logo || '#246990'}
                    onChange={(e) => handleInputChange('couleur_logo', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <Input
                    value={schoolInfo.couleur_logo || '#246990'}
                    onChange={(e) => handleInputChange('couleur_logo', e.target.value)}
                    placeholder="#246990"
                    className="flex-1 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                </div>
              </div>

              {/* Le plafond des réductions et les familles nombreuses vivaient
                  ici, entre le logo et l'adresse. Ils bornent de l'argent : ils
                  sont partis dans « Paramétrage de la scolarité ». */}

            </div>

            {/* Colonne droite - Coordonnées */}
            <div className="space-y-6">
              
              {/* Adresse */}
              <div className="space-y-4">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <MapPin size={16} className="text-green-600" />
                  Adresse complète
                </Label>
                <div className="space-y-3">
                  <Input
                    value={schoolInfo.address || ''}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Numéro et rue"
                    className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={schoolInfo.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Ville"
                      className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                    />
                    <Input
                      value={schoolInfo.postalCode || ''}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      placeholder="Code postal"
                      className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                    />
                  </div>
                  <Input
                    value={schoolInfo.country || ''}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="Pays"
                    className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Phone size={16} className="text-purple-600" />
                  Coordonnées
                </Label>
                <div className="space-y-3">
                  <Input
                    value={schoolInfo.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Téléphone"
                    className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                  <Input
                    value={schoolInfo.fax || ''}
                    onChange={(e) => handleInputChange('fax', e.target.value)}
                    placeholder="Fax"
                    className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                  <Input
                    type="email"
                    value={schoolInfo.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Email institutionnel"
                    className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                  <Input
                    value={schoolInfo.website || ''}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="Site web"
                    className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                </div>
              </div>

            </div>
          </div>

          </CarteReglages>

          {/* Aperçu du header */}
          <CarteReglages titre="Aperçu" accent="#319190">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Aperçu dans le header :</h3>
            <div className="bg-gradient-to-r from-[#246990] to-[#3084b5] p-4 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center p-0.5 shadow-sm">
                  {schoolInfo.logo_url ? (
                    <img 
                      src={schoolInfo.logo_url}
                      alt="Logo"
                      className="w-full h-full object-contain rounded"
                    />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center font-bold text-lg"
                      style={{ color: schoolInfo.couleur_logo || '#246990' }}
                    >
                      {schoolInfo.logo_initiales || 'EC'}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xl font-bold text-white leading-tight">
                    {schoolInfo.name || 'Nom de l\'école'}
                  </div>
                  <div className="text-sm text-white/90 leading-tight">
                    {schoolInfo.sous_titre || 'Établissement Scolaire'}
                  </div>
                  {schoolInfo.slogan && (
                    <div className="text-xs text-white/80 leading-tight">
                      {schoolInfo.slogan}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <h3 className="text-sm font-medium text-gray-700 mb-3 mt-4">Aperçu page d'accueil :</h3>
                <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                    <div className="text-center">
                    <h1 className="text-xl font-bold text-[#246990] mb-1">
                        {schoolInfo.titre_accueil || 'Bienvenue dans votre espace'}
                    </h1>
                    <p className="text-gray-600 text-sm">
                        {schoolInfo.description_accueil || 'Les informations essentielles pour suivre la scolarité de vos enfants'}
                    </p>
                    </div>
                </div>
          </CarteReglages>

          {/* Bouton de sauvegarde */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="h-9 min-w-[168px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:bg-gray-300"
            >
              <Save className={`h-4 w-4 ${saving ? 'animate-spin' : ''}`} />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </div>

      {/* Dialog de confirmation pour suppression logo */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteLogo}
        title="Supprimer le logo"
        description="Êtes-vous sûr de vouloir supprimer le logo de l'école ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning"
      />

      {/* Toast */}
      {showToast && (
        <Toast
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default SchoolSettingsSection;
